import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@/lib/schemas';
import { useForgotPassword } from '@/hooks/use-auth';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export function ForgotPasswordPage() {
    const { t } = useI18n();
    const forgotPassword = useForgotPassword();
    const [sent, setSent] = useState(false);
    const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordInput>({
        resolver: zodResolver(forgotPasswordSchema),
        defaultValues: { email: '' },
    });

    const onSubmit = async (data: ForgotPasswordInput) => {
        try {
            await forgotPassword.mutateAsync(data);
            setSent(true);
        } catch {
            // The mutation state renders the generic error without leaking account details.
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <CardTitle>{t('auth.forgotPasswordTitle')}</CardTitle>
                    <CardDescription>{t('auth.forgotPasswordDescription')}</CardDescription>
                </CardHeader>
                {sent ? (
                    <>
                        <CardContent className="text-center space-y-4">
                            <CheckCircle2 className="h-12 w-12 text-status-success mx-auto" />
                            <p className="text-sm text-foreground-secondary">{t('auth.resetEmailSent')}</p>
                        </CardContent>
                        <CardFooter>
                            <Button asChild className="w-full">
                                <Link to="/login">{t('auth.backToLogin')}</Link>
                            </Button>
                        </CardFooter>
                    </>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="reset-email">{t('auth.email')}</Label>
                                <Input
                                    id="reset-email"
                                    type="email"
                                    autoComplete="email"
                                    placeholder={t('auth.emailPlaceholder')}
                                    {...register('email')}
                                />
                                {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
                            </div>
                            {forgotPassword.isError && (
                                <p className="text-sm text-destructive">{t('error.somethingWentWrong')}</p>
                            )}
                        </CardContent>
                        <CardFooter className="flex flex-col gap-3">
                            <Button type="submit" className="w-full" disabled={forgotPassword.isPending}>
                                {forgotPassword.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {forgotPassword.isPending ? t('auth.sendingResetLink') : t('auth.sendResetLink')}
                            </Button>
                            <Button asChild variant="ghost" className="w-full">
                                <Link to="/login">
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    {t('auth.backToLogin')}
                                </Link>
                            </Button>
                        </CardFooter>
                    </form>
                )}
            </Card>
        </div>
    );
}
